import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import { Box, Popper, useTheme } from "@mui/material";
import { useEventListener } from "@react-hookz/web";
import {
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  getDOMSelection,
} from "lexical";
import { useCallback, useEffect, useState } from "react";
import { serviceURL } from "../../../../../utils/service-url";
import { useLexicalContext } from "../../lexical-context";
import { $isDesignHuddleNodeMui } from "../../nodes/design-huddle-mui";
import { $isImageNodeMui } from "../../nodes/image-node-mui";
import { $isPollNode } from "../../nodes/poll/helpers";
import { $isSurveyLinkNode } from "../../nodes/survey-link-node";
import { $isVideoNodeMui } from "../../nodes/video-node-mui";
import { CoPilotPanel } from "../co-pilot/co-pilot-panel";
import { CoPilotToolbarContext } from "../co-pilot/co-pilot-toolbar-context";
import { ToolbarElement } from "../generic-toolbar/toolbar-element";
import { useLexicalToolbar } from "../toolbar/hooks/use-lexical-toolbar";
import { useGenericFloatingToolbar } from "./hooks";
import { getSelectedRangeNode } from "./utils";

import type { EditorModalState } from "../../lexical-context";
import type { CoPilotPanelParams } from "../co-pilot/co-pilot-toolbar-context";
import type { GenericToolbarElement, NodeTypeToolbarElements } from "../generic-toolbar/types";
import type { LexicalEditor } from "lexical";
import type { FC, RefObject } from "react";

interface GenericFloatingToolbarProps {
  disabled?: boolean;
  channel: "slack" | "teams" | "email" | "text";
  editorScrollRef?: RefObject<HTMLDivElement | null>;
}

export const GenericFloatingToolbar: FC<
  GenericFloatingToolbarProps & {
    floatingElementsByNodeType: NodeTypeToolbarElements;
  }
> = ({ floatingElementsByNodeType, ...props }) => {
  const {
    focus: { focused },
    editorModal: { open: modalOpen, handleOpen: openModal },
  } = useLexicalContext();
  const [toolbarElements, setToolbarElements] = useState<GenericToolbarElement[]>([]);
  const [panelState, setPanelState] = useState<CoPilotPanelParams | null>(null);
  const [editor] = useLexicalComposerContext();

  const sortOfFocused = focused || modalOpen;

  const openPanel = useCallback((params: CoPilotPanelParams) => setPanelState(params), []);
  const closePanel = useCallback(() => setPanelState(null), []);

  const updatePopup = useCallback(() => {
    editor.getEditorState().read(() => {
      // Should not to pop up the floating toolbar when using IME input
      if (editor.isComposing()) {
        setToolbarElements([]);
        return;
      }
      const selection = $getSelection();
      const nativeSelection = getDOMSelection(editor._window);
      const rootElement = editor.getRootElement();

      // No node
      if (!nativeSelection || !rootElement) {
        setToolbarElements([]);
        return;
      }

      // Text Node
      if ($isRangeSelection(selection) && rootElement.contains(nativeSelection.anchorNode)) {
        const node = getSelectedRangeNode(selection);

        let shouldOpen =
          selection.getTextContent() !== "" && ($isTextNode(node) || $isParagraphNode(node));

        const rawTextContent = selection.getTextContent().replace(/\n/g, "");
        if (!selection.isCollapsed() && rawTextContent === "") {
          shouldOpen = false;
        }
        if (shouldOpen && floatingElementsByNodeType.text) {
          setToolbarElements(floatingElementsByNodeType.text);
          return;
        }
      }

      // Image node
      const singleNode = selection?.getNodes().length === 1 ? selection.getNodes()[0] : null;

      if ($isImageNodeMui(singleNode) && sortOfFocused && !singleNode.getIsCurrentlyResizing()) {
        if (singleNode.__sourceAssetId && floatingElementsByNodeType.savedImage) {
          setToolbarElements(floatingElementsByNodeType.savedImage);
          return;
        } else if (!singleNode.__sourceAssetId && floatingElementsByNodeType.unsavedImage) {
          setToolbarElements(
            floatingElementsByNodeType.unsavedImage.filter(
              (element) =>
                // Disable save image button for external links
                singleNode.__url.startsWith(serviceURL("images")) ||
                element.type !== "saveImageButton",
            ),
          );
          return;
        }
      }

      if ($isVideoNodeMui(singleNode) && sortOfFocused && !singleNode.getIsCurrentlyResizing()) {
        if (singleNode.__sourceAssetId && floatingElementsByNodeType.videoAsset) {
          setToolbarElements(floatingElementsByNodeType.videoAsset);
          return;
        }
      }

      // Design Huddle node
      if ($isDesignHuddleNodeMui(singleNode)) {
        let elements =
          singleNode.__sourceAssetId === undefined
            ? floatingElementsByNodeType.unsavedDesignHuddle
            : floatingElementsByNodeType.savedDesignHuddle;
        // needs a refactor but if there is a special condition on the element, filter for it
        if (singleNode.__error) {
          elements = elements?.filter((element) => element.type === "deleteNodeButton") || null;
        }

        if (elements && !singleNode.getIsCurrentlyResizing()) {
          setToolbarElements(elements);
          return;
        }
      }

      // Poll node
      if ($isPollNode(singleNode)) {
        const elements = floatingElementsByNodeType.poll;
        if (elements) {
          setToolbarElements(elements);
          return;
        }
      }

      // Survey link node ( the selection is the intternal text node of the survey link )
      if ($isSurveyLinkNode(singleNode?.getParent())) {
        const elements = floatingElementsByNodeType.surveyLink;
        if (elements) {
          setToolbarElements(elements);
          return;
        }
      }

      setToolbarElements([]);
    });
  }, [editor, floatingElementsByNodeType, setToolbarElements, sortOfFocused]);

  useEventListener(document, "selectionchange", updatePopup);

  useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(() => updatePopup()),
        editor.registerRootListener(() => {
          if (editor.getRootElement() === null) {
            setToolbarElements([]);
          }
        }),
      ),
    [editor, updatePopup],
  );

  useEffect(() => {
    if (focused) updatePopup();
  }, [focused, updatePopup]);

  if (!toolbarElements.length && panelState === null) return null;
  return (
    <CoPilotToolbarContext.Provider value={{ openPanel, closePanel, panelState }}>
      {toolbarElements.length > 0 && (
        <GenericFloatingToolbarInner
          editor={editor}
          openModal={openModal}
          {...props}
          floatingElements={toolbarElements}
        />
      )}
      <CoPilotPanel />
    </CoPilotToolbarContext.Provider>
  );
};

const GenericFloatingToolbarInner: FC<
  GenericFloatingToolbarProps & {
    openModal: (modalState: EditorModalState) => void;
    editor: LexicalEditor;
    floatingElements: GenericToolbarElement[];
  }
> = ({ channel, disabled, floatingElements, openModal, editorScrollRef, editor }) => {
  const theme = useTheme();
  const { anchorEl, open } = useGenericFloatingToolbar({
    editor,
    editorScrollRef,
    channel,
  });
  const toolbarState = useLexicalToolbar(channel);

  if (!editor.isEditable()) return null;
  return (
    <Popper
      open={open}
      placement="top-start"
      anchorEl={anchorEl}
      disablePortal
      style={{ zIndex: theme.zIndex.footer - 1 }}
      modifiers={[
        {
          name: "offset",
          enabled: true,
          options: { offset: [0, 5] },
        },
        {
          name: "flip",
          enabled: true,
          options: { boundary: editorScrollRef?.current },
        },
      ]}
    >
      <Box
        sx={{
          display: "flex",
          bgcolor: "background.paper",
          p: 0.5,
          boxShadow: 3,
          borderRadius: 1,
          transition: "opacity 0.5s",
          height: "35px",
          willChange: "transform",
        }}
      >
        {floatingElements.map((element, index) => (
          <ToolbarElement
            element={element}
            key={index}
            disabled={disabled}
            toolbarState={toolbarState}
            openModal={openModal}
            mode="floating"
          />
        ))}
      </Box>
    </Popper>
  );
};
